/* eslint-disable jsx-a11y/img-redundant-alt */
import React, { useEffect } from "react";
import Footer from "../components/Footer";
import NewNavbar from "../components/NewNavbar";
import evenessBlogImagePreview from "../assets/blog-details-image-preview.png";
import { Link, useParams } from "react-router-dom";
import { usePublicBlogsReadQuery } from "../store/generated.ts";
import { ReactComponent as BlogDetailsIcon } from "./../assets/svgs/blog-details-icon.svg";
import { ReactComponent as ItemNotFoundIcon } from "./../assets/svgs/item-not-found.svg";
import SEO from "../components/SEO";

const cleanMetaText = (value) =>
  String(value || "")
    .replace(/<[^>]*>/g, " ")
    .replace(/\s+/g, " ")
    .trim();

const isReactSnap =
  typeof navigator !== "undefined" && navigator.userAgent === "ReactSnap";

const articleContentClass = `
  max-w-none font-inter text-[14px] leading-[1.8] text-[#444444]
  md:text-[16px]

  [&_p]:my-4
  [&_p]:leading-[1.8]

  [&_h1]:mb-4
  [&_h1]:mt-8
  [&_h1]:font-helvetica
  [&_h1]:text-[32px]
  [&_h1]:font-semibold
  [&_h1]:leading-[1.1]
  [&_h1]:tracking-[-1.2px]
  [&_h1]:text-black

  [&_h2]:mb-4
  [&_h2]:mt-9
  [&_h2]:font-helvetica
  [&_h2]:text-[28px]
  [&_h2]:font-semibold
  [&_h2]:leading-[1.15]
  [&_h2]:tracking-[-1px]
  [&_h2]:text-black

  [&_h3]:mb-3
  [&_h3]:mt-7
  [&_h3]:font-helvetica
  [&_h3]:text-[22px]
  [&_h3]:font-semibold
  [&_h3]:leading-tight
  [&_h3]:text-black

  [&_ol]:my-5
  [&_ol]:list-decimal
  [&_ol]:pl-6

  [&_ul]:my-5
  [&_ul]:list-disc
  [&_ul]:pl-6

  [&_li]:my-2
  [&_li]:pl-1

  [&_img]:mx-auto
  [&_img]:my-8
  [&_img]:w-full
  [&_img]:rounded-[5px]

  [&_a]:text-black
  [&_a]:underline
  [&_a]:underline-offset-4

  [&_strong]:font-semibold
  [&_strong]:text-black
`;

const BlogHeaderLabel = () => {
  return (
    <div className="mb-4 flex flex-row items-center justify-center gap-2">
      <BlogDetailsIcon />
      <p className="font-spaceMono text-[8px] font-bold uppercase tracking-[1.35px] text-[#71717B] md:text-[9px]">
        The Eveness Journal
      </p>
    </div>
  );
};

const EvenessBlogDetails = () => {
  const { slug } = useParams();

  useEffect(() => {
    if (!isReactSnap && typeof window !== "undefined") {
      window.scrollTo({ top: 0, behavior: "smooth" });
    }
  }, [slug]);

  const { data, isLoading, isError, error: blogError, } = usePublicBlogsReadQuery(
    { slug: String(slug) },
    {
      skip: !slug,
      refetchOnMountOrArgChange: !isReactSnap,
    },
  );

  const blogErrorStatus = Number(
    blogError?.status ??
      blogError?.originalStatus ??
      blogError?.data?.status,
  );
  const isBlogNotFound = isError && blogErrorStatus === 404;

  const heroSrc = data?.hero_image_url || evenessBlogImagePreview;
  const title = data?.title || "";
  const contentHtml = data?.content_md || data?.content || "";
  const previewText = cleanMetaText(data?.preview || "").slice(0, 220);

  const blogSeoTitle = title ? `${title} | Eveness Journal` : "Eveness Journal";
  const blogSeoDescription =
    cleanMetaText(data?.preview || contentHtml).slice(0, 180) ||
    "Read fashion insights, styling tips, and personalized shopping guidance from the Eveness Journal.";

  const renderSeo = (
    <SEO
      title={blogSeoTitle}
      description={blogSeoDescription}
      path={`/blog/${slug}`}
      image={heroSrc}
      type="article"
    />
  );

  const renderBlogNotFound = ({ includeLayout = true } = {}) => (
    <>
      {renderSeo}
      <div className="flex min-h-screen flex-col bg-white text-black">
        {includeLayout ? <NewNavbar /> : null}
        <main className="flex flex-1 items-center justify-center px-5 py-16">
          <div className="mx-auto w-full max-w-[520px] text-center">
            <div className="mx-auto flex h-[88px] w-[88px] items-center justify-center rounded-full bg-[#F4F4F4]">
              <ItemNotFoundIcon/>
            </div>
            <h1 className="mt-6 font-helvetica text-[28px] font-semibold text-[#0F0F0F] sm:text-[34px]">
              Blog not found
            </h1>
            <p className="mx-auto mt-3 max-w-[420px] font-inter text-sm leading-6 text-[#80808C] sm:text-base">
              This blog may have been removed, is no longer available, or the
              blog link may be incorrect.
            </p>
            <Link
              to="/blog"
              replace
              className="mt-7 inline-flex h-11 items-center justify-center rounded-[6px] bg-[#252525] px-6 font-inter text-sm font-medium text-white transition hover:bg-[#3A3A3A] focus:outline-none focus:ring-2 focus:ring-[#252525] focus:ring-offset-2"
            >
              Back to journal
            </Link>
          </div>
        </main>
        {includeLayout ? <Footer /> : null}
      </div>
    </>
  );

  if (isReactSnap) {
    if (isLoading) {
      return <>{renderSeo}</>;
    }

    if (isBlogNotFound) return renderBlogNotFound({ includeLayout: false });

    if (isError || !data) {
      return (
        <>
          {renderSeo}
          <div className="min-h-screen bg-[#F4F4F4] text-black">
            <div className="mx-auto max-w-[1080px] px-4 py-10">
              <div className="rounded-[5px] border border-[#CFCFCF] bg-white p-6 font-helvetica text-sm text-red-500">
                Failed to load blog.
              </div>
            </div>
          </div>
        </>
      );
    }

    return (
      <>
        {renderSeo}
        <div className="min-h-screen bg-[#F4F4F4] text-black">
          <div className="mx-auto max-w-[1080px] px-4 py-10">
            <article className="rounded-[5px] border border-[#D8D8D8] bg-white px-4 py-8 md:px-8 md:py-10">
              <header className="mx-auto max-w-[820px] text-center">
                <BlogHeaderLabel />

                <h1 className="font-helvetica text-[34px] font-semibold leading-[1.05] tracking-[-1.6px] text-black sm:text-[42px] md:text-[56px]">
                  {title}
                </h1>

                {previewText ? (
                  <p className="mx-auto mt-5 max-w-[620px] font-inter text-[14px] leading-[1.7] text-[#666666] md:text-[16px]">
                    {previewText}
                  </p>
                ) : null}
              </header>

              <figure className="mx-auto mt-8 w-full max-w-[880px] overflow-hidden rounded-[6px] bg-[#F2F2F2]">
                <img
                  src={heroSrc}
                  alt={title || "Blog hero image"}
                  className="h-auto max-h-[520px] w-full object-contain"
                />
              </figure>

              <div className="mx-auto mt-10 max-w-[760px] border-t border-[#E5E5E5] pt-8">
                <div
                  className={articleContentClass}
                  dangerouslySetInnerHTML={{ __html: contentHtml }}
                />
              </div>
            </article>
          </div>
        </div>
      </>
    );
  }

  if (isBlogNotFound) return renderBlogNotFound();

  return (
    <>
      {renderSeo}

      <div className="min-h-screen bg-[#F4F4F4] text-black">
        <NewNavbar />

        <main className="mx-auto mt-4 w-full max-w-[1080px] px-3 pb-16">
          <article className="rounded-[5px] border border-[#D8D8D8] bg-white px-4 py-8 md:px-8 md:py-10">
            <header className="mx-auto max-w-[820px] text-center">
              <BlogHeaderLabel />

              {isLoading ? (
                <div className="mx-auto max-w-[720px]">
                  <div className="mx-auto h-10 w-full rounded-[5px] bg-[#EEF2F5] md:h-14" />
                  <div className="mx-auto mt-3 h-10 w-8/12 rounded-[5px] bg-[#EEF2F5] md:h-14" />
                  <div className="mx-auto mt-6 h-4 w-9/12 rounded-[5px] bg-[#EEF2F5]" />
                  <div className="mx-auto mt-2 h-4 w-7/12 rounded-[5px] bg-[#EEF2F5]" />
                </div>
              ) : (
                <>
                  <h1 className="font-montserrat text-[24px] font-semibold leading-[100%] tracking-[-1.2px] text-black sm:text-[36px] md:text-[42px]">
                    {title}
                  </h1>

                  {previewText ? (
                    <p className="mx-auto mt-5 max-w-[620px] font-inter text-[14px] leading-[1.7] text-[#666666] md:text-[16px]">
                      {previewText}
                    </p>
                  ) : null}
                </>
              )}

              {isError && (
                <div className="mt-5 font-helvetica text-sm text-red-500">
                  Failed to load blog. Please try again.
                </div>
              )}
            </header>

            <figure className="mx-auto mt-8 w-full max-w-[880px] overflow-hidden rounded-[6px] bg-[#F2F2F2]">
              {isLoading ? (
                <div className="h-[260px] w-full bg-[#EEF2F5] md:h-[420px]" />
              ) : (
                <img
                  src={heroSrc}
                  alt={title || "Blog hero image"}
                  className="h-auto max-h-[520px] w-full object-contain"
                />
              )}
            </figure>

            <div className="mx-auto mt-10 max-w-[760px] border-t border-[#E5E5E5] pt-8">
              {isLoading ? (
                <div className="space-y-3">
                  <div className="h-4 w-full rounded bg-[#EEF2F5]" />
                  <div className="h-4 w-11/12 rounded bg-[#EEF2F5]" />
                  <div className="h-4 w-10/12 rounded bg-[#EEF2F5]" />
                  <div className="mt-6 h-4 w-full rounded bg-[#EEF2F5]" />
                  <div className="h-4 w-9/12 rounded bg-[#EEF2F5]" />
                </div>
              ) : (
                <div
                  className={articleContentClass}
                  dangerouslySetInnerHTML={{ __html: contentHtml }}
                />
              )}
            </div>
          </article>
        </main>

        <Footer />
      </div>
    </>
  );
};

export default EvenessBlogDetails;
